import type { Order, OrderStatus, Product } from '@shop-monorepo/types';

const ORDER_STATUSES: OrderStatus[] = [
  'pending',
  'confirmed',
  'paid',
  'shipped',
  'received',
  'refused',
  'cancelled',
];
import type { TableProps } from 'antd';
import { Select, Spin } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';

import { OrderMobileCard } from '../components/OrderMobileCard';
import { OrderStatusTag } from '../components/OrderStatusTag';
import {
  FormCard,
  PageContainer,
  PageHeader,
  PageTitle,
  StyledTable,
} from '../components/PageLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsMobile } from '../hooks/useIsMobile';
import { useStats } from '../hooks/useStatsQueries';
import { ROUTES } from '../routes/routes';
import { formatPrice } from '../utils/currency';

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 280px;
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 8px 0 4px;
`;

const KpiLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const ChartTitle = styled(SectionTitle)``;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #edf2f7;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusCount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
`;

const TooltipBox = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 8px 12px;
`;

const TooltipLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const TooltipValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
`;

const TooltipDivider = styled.div`
  border-top: 1px solid #2d2d2d;
  margin: 6px 0;
`;

const TooltipCount = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const ProductRank = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;

  &:last-child {
    border-bottom: none;
  }
`;

const ProductRankIndex = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ff8e8e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ProductRankName = styled.a`
  font-size: 13px;
  color: #1a1a1a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #ff8e8e;
  }
`;

const ProductRankCount = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  flex-shrink: 0;
`;

type ChartEntry = { count?: number };

const CustomTooltip = (props: Record<string, unknown>) => {
  const { active, payload, label } = props as {
    active?: boolean;
    payload?: { value?: number; payload?: ChartEntry }[];
    label?: string;
  };
  if (!active || !payload?.length) return null;
  const entry = payload[0]!;
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      <TooltipValue>{formatPrice(entry.value ?? 0)}</TooltipValue>
      <TooltipDivider />
      <TooltipCount>{entry.payload?.count ?? 0} замовлень</TooltipCount>
    </TooltipBox>
  );
};

const MONTH_LABELS = [
  'Січ',
  'Лют',
  'Бер',
  'Кві',
  'Тра',
  'Чер',
  'Лип',
  'Сер',
  'Вер',
  'Жов',
  'Лис',
  'Гру',
];

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data: stats, isLoading } = useStats(year);
  const isMobile = useIsMobile();

  const monthData = (stats?.monthlyRevenue ?? []).map((entry) => ({
    ...entry,
    monthLabel: MONTH_LABELS[entry.month - 1],
  }));

  const yearOptions = [currentYear - 2, currentYear - 1, currentYear].map((y) => ({
    value: y,
    label: String(y),
  }));

  if (isLoading) {
    return (
      <PageContainer>
        <Spin size="large" />
      </PageContainer>
    );
  }

  const recentOrderColumns: TableProps<Order>['columns'] = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => (
        <a onClick={() => navigate(ROUTES.ORDER_VIEW.replace(':id', String(id)))}>
          {String(id).padStart(4, '0')}
        </a>
      ),
    },
    {
      title: 'Отримувач',
      key: 'recipient',
      render: (_: unknown, record: Order) =>
        `${record.recipient.lastName} ${record.recipient.firstName}`,
    },
    {
      title: 'Сума',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => formatPrice(total),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: Order['status']) => <OrderStatusTag status={status} />,
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) =>
        new Date(date).toLocaleDateString('uk-UA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Панель управління</PageTitle>
        <Select
          value={year}
          onChange={setYear}
          options={yearOptions}
          size="small"
          style={{ width: 90 }}
        />
      </PageHeader>

      <KpiGrid>
        <FormCard>
          <KpiLabel>Дохід (підтверджені)</KpiLabel>
          <KpiValue>{formatPrice(stats?.orders.revenue ?? 0)}</KpiValue>
        </FormCard>
        <FormCard>
          <KpiLabel>Замовлення</KpiLabel>
          <KpiValue>{stats?.orders.total ?? 0}</KpiValue>
        </FormCard>
        <FormCard>
          <KpiLabel>Клієнти</KpiLabel>
          <KpiValue>{stats?.clients.total ?? 0}</KpiValue>
        </FormCard>
        <FormCard>
          <KpiLabel>Замовлено продуктів</KpiLabel>
          <KpiValue>{stats?.products.ordered ?? 0}</KpiValue>
        </FormCard>
      </KpiGrid>

      <BottomGrid>
        <FormCard>
          <SectionTitle>Замовлення за статусом</SectionTitle>
          {ORDER_STATUSES.map((status) => (
            <StatusRow key={status}>
              <OrderStatusTag status={status} />
              <StatusCount>{stats?.orders.byStatus[status] ?? 0}</StatusCount>
            </StatusRow>
          ))}
        </FormCard>

        <FormCard>
          <SectionTitle>Останні замовлення</SectionTitle>
          {isMobile ? (
            (stats?.recentOrders ?? []).map((order) => (
              <OrderMobileCard
                key={order.id}
                order={order}
                onView={(id) => navigate(ROUTES.ORDER_VIEW.replace(':id', String(id)))}
              />
            ))
          ) : (
            <StyledTable
              columns={recentOrderColumns}
              dataSource={stats?.recentOrders ?? []}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 500 }}
            />
          )}
        </FormCard>

        <FormCard>
          <SectionTitle>Топ-5 продуктів</SectionTitle>
          {(stats?.topProducts ?? []).map((product: Product, index: number) => (
            <ProductRank key={product.id}>
              <ProductRankIndex>{index + 1}</ProductRankIndex>
              <ProductRankName
                title={product.name}
                onClick={() => navigate(ROUTES.PRODUCT_VIEW.replace(':slug', product.slug))}
              >
                {product.name}
              </ProductRankName>
              <ProductRankCount>{product.ordersCount ?? 0}</ProductRankCount>
            </ProductRank>
          ))}
        </FormCard>
      </BottomGrid>

      <FormCard>
        <ChartTitle>Виручка по місяцях</ChartTitle>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthData} margin={{ top: isMobile ? 24 : 48 }}>
            <Bar dataKey="revenue" fill="#ff8e8e">
              {!isMobile && (
                <LabelList
                  content={(props) => {
                    const { x, y, width, value, index } = props as {
                      x?: number;
                      y?: number;
                      width?: number;
                      value?: number;
                      index?: number;
                    };
                    const entry = index !== undefined ? monthData[index] : undefined;
                    if (!value || !entry) return null;
                    return (
                      <g>
                        <text
                          x={(x ?? 0) + (width ?? 0) / 2}
                          y={(y ?? 0) - 18}
                          textAnchor="middle"
                          fill="#94a3b8"
                          fontSize={10}
                        >
                          {entry.count} зам.
                        </text>
                        <text
                          x={(x ?? 0) + (width ?? 0) / 2}
                          y={(y ?? 0) - 5}
                          textAnchor="middle"
                          fill="#1a1a1a"
                          fontSize={11}
                          fontWeight={700}
                        >
                          {formatPrice(value)}
                        </text>
                      </g>
                    );
                  }}
                />
              )}
            </Bar>
            <XAxis dataKey="monthLabel" tick={{ fontSize: isMobile ? 10 : 12 }} />
            {!isMobile && (
              <YAxis tickFormatter={(v) => formatPrice(v)} tick={{ fontSize: 11 }} width={80} />
            )}
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          </BarChart>
        </ResponsiveContainer>
      </FormCard>
    </PageContainer>
  );
};
